import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Group,
  Text,
  Button,
  Anchor,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import {
  IconFingerprint,
  IconBolt,
  IconShieldLock,
  IconShieldCheck,
  IconCheck,
  IconChevronRight,
  IconAlertTriangle,
} from "@tabler/icons-react";
import useLocale from "../hooks/useLocale";

const BENEFITS = {
  create: [
    { key: "fast", icon: IconBolt },
    { key: "private", icon: IconShieldLock },
  ],
};

const HERO_BY_STATE = {
  prompt: { Icon: IconFingerprint, tone: "neutral" },
  loading: { Icon: IconFingerprint, tone: "neutral" },
  success: { Icon: IconCheck, tone: "success" },
  error: { Icon: IconAlertTriangle, tone: "error" },
};

const PasskeyPrompt = ({
  mode = "use",
  state = "prompt",
  onContinue,
  onSkip,
}) => {
  const theme = useMantineTheme();
  const locale = useLocale("passkey");
  const modeCopy = locale[mode] || locale.use;
  const copy = modeCopy[state] || modeCopy.prompt;

  const cardBg =
    theme.colorScheme === "dark" ? theme.colors.dark[6] : "#fff";
  const mutedText =
    theme.colorScheme === "dark"
      ? theme.colors.dark[2]
      : theme.colors.gray[7];
  const accentColor =
    theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6];
  const neutralIconBg =
    theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[1];
  const neutralIconColor =
    theme.colorScheme === "dark" ? theme.white : theme.black;
  const successIconBg =
    theme.colorScheme === "dark"
      ? "rgba(64, 192, 87, 0.18)"
      : theme.colors.green[1];
  const successIconColor =
    theme.colorScheme === "dark" ? theme.colors.green[4] : theme.colors.green[7];
  const errorIconBg =
    theme.colorScheme === "dark"
      ? "rgba(240, 62, 62, 0.18)"
      : theme.colors.red[1];
  const errorIconColor =
    theme.colorScheme === "dark" ? theme.colors.red[4] : theme.colors.red[7];

  const heroConfig = HERO_BY_STATE[state] || HERO_BY_STATE.prompt;
  const HeroIcon = heroConfig.Icon;
  const heroBg =
    heroConfig.tone === "success"
      ? successIconBg
      : heroConfig.tone === "error"
        ? errorIconBg
        : neutralIconBg;
  const heroColor =
    heroConfig.tone === "success"
      ? successIconColor
      : heroConfig.tone === "error"
        ? errorIconColor
        : neutralIconColor;

  // Use-mode prompt swaps the info cards + bottom CTAs for a pair of
  // clickable action rows ("Use Passkey" / "Verify another way") with
  // chevrons. Everything else keeps the standard layout.
  const useActionList = mode === "use" && state === "prompt";
  const showBenefits = state === "prompt" && !useActionList;
  const showFooter = !useActionList;
  const showButtonIcon = state === "prompt" || state === "loading";
  const isLoading = state === "loading";
  const benefits = BENEFITS[mode] || BENEFITS.create;

  // Celebratory intro before the create prompt: a centered green check + the
  // success line, then a smooth crossfade into the main UI. Runs once on
  // mount of the create-prompt screen; subsequent state changes (loading,
  // success, error) skip it.
  const isCreatePrompt = mode === "create" && state === "prompt";
  const [introMounted, setIntroMounted] = useState(isCreatePrompt);
  const [introVisible, setIntroVisible] = useState(false);

  useEffect(() => {
    if (!isCreatePrompt) return;
    const showTimer = setTimeout(() => setIntroVisible(true), 30);
    const hideTimer = setTimeout(() => setIntroVisible(false), 1500);
    const unmountTimer = setTimeout(() => setIntroMounted(false), 2000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(unmountTimer);
    };
  }, [isCreatePrompt]);

  const introBg =
    theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white;

  return (
    <Box
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "calc(100% + 20px)",
        margin: "-20px -25px 0 -25px",
      }}
    >
      {introMounted && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: introBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: introVisible ? 1 : 0,
            transition: "opacity 500ms ease",
            zIndex: 10,
            pointerEvents: introVisible ? "auto" : "none",
          }}
        >
          <Stack align="center" spacing={20}>
            <Box
              style={{
                width: 120,
                height: 120,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 28px rgba(34, 197, 94, 0.4)",
                transform: introVisible ? "scale(1)" : "scale(0.6)",
                transition:
                  "transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 400ms ease",
              }}
            >
              <IconCheck size={68} stroke={3} color="#000" />
            </Box>
            <Text fw={700} fz={26} ta="center">
              {locale.create?.verifiedIntro || "You’ve been verified"}
            </Text>
          </Stack>
        </Box>
      )}
      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          opacity: introMounted ? 0 : 1,
          transition: "opacity 500ms ease",
        }}
      >
      {/* Content */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "20px 24px 12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box style={{ position: "relative", marginBottom: 14 }}>
          <ThemeIcon
            size={68}
            radius="xl"
            style={{
              backgroundColor:
                mode === "create" && state === "prompt"
                  ? theme.colorScheme === "dark"
                    ? "rgba(74, 222, 128, 0.15)"
                    : "#dcfce7"
                  : heroBg,
              color:
                mode === "create" && state === "prompt"
                  ? theme.colorScheme === "dark"
                    ? theme.white
                    : "#000"
                  : heroColor,
            }}
          >
            <HeroIcon
              size={36}
              stroke={1.6}
              color={
                mode === "create" && state === "prompt"
                  ? theme.colorScheme === "dark"
                    ? theme.white
                    : "#000"
                  : undefined
              }
            />
          </ThemeIcon>
          {mode === "create" && state === "prompt" && (
            <Box
              style={{
                position: "absolute",
                right: -2,
                bottom: -2,
                width: 26,
                height: 26,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                color: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid ${
                  theme.colorScheme === "dark" ? theme.colors.dark[7] : "#fff"
                }`,
                boxShadow: "0 2px 6px rgba(34, 197, 94, 0.45)",
              }}
            >
              <IconCheck size={14} stroke={3} color="#000" />
            </Box>
          )}
        </Box>

        <Text fw={700} fz={22} lh={1.2} mb={6}>
          {copy.title}
        </Text>
        <Text fz={13} c={mutedText} lh={1.5} mb={18} style={{ width: "100%" }}>
          {copy.subtitle}
        </Text>

        {useActionList && (
          <Stack spacing={10} style={{ width: "100%" }}>
            {[
              {
                key: "use",
                icon: IconFingerprint,
                title: copy.actions?.use?.title || copy.primaryButton,
                description: copy.actions?.use?.description,
                onClick: onContinue,
              },
              {
                key: "other",
                icon: IconShieldCheck,
                title: copy.actions?.other?.title || copy.skipButton,
                description: copy.actions?.other?.description,
                onClick: onSkip,
              },
            ].map(({ key, icon: ActionIcon, title, description, onClick }) => (
              <Box
                component="button"
                type="button"
                key={key}
                onClick={onClick}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: 12,
                  backgroundColor: "transparent",
                  color: theme.colorScheme === "dark" ? theme.white : "#000",
                  border: `1px solid ${
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[4]
                      : theme.colors.gray[3]
                  }`,
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                }}
              >
                <ThemeIcon
                  size={36}
                  radius="md"
                  style={{
                    flexShrink: 0,
                    backgroundColor: "transparent",
                    color: theme.colorScheme === "dark" ? theme.white : "#000",
                  }}
                >
                  <ActionIcon
                    size={22}
                    stroke={1.8}
                    color={theme.colorScheme === "dark" ? theme.white : "#000"}
                  />
                </ThemeIcon>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} fz={14} lh={1.3} style={{ color: "inherit" }}>
                    {title}
                  </Text>
                  {description && (
                    <Text fz={12} c={mutedText} lh={1.4} mt={2}>
                      {description}
                    </Text>
                  )}
                </Box>
                <IconChevronRight
                  size={18}
                  stroke={2}
                  color={mutedText}
                  style={{ flexShrink: 0 }}
                />
              </Box>
            ))}
          </Stack>
        )}

        {showBenefits && (
          <Stack spacing={10} style={{ width: "100%" }}>
            {benefits.map(({ key, icon: Icon }) => {
              const benefit = copy.benefits?.[key];
              if (!benefit) return null;
              return (
                <Group
                  key={key}
                  spacing={14}
                  noWrap
                  align="flex-start"
                  style={{
                    backgroundColor: cardBg,
                    borderRadius: 12,
                    padding: "12px 14px",
                    border: `1px solid ${
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[4]
                        : theme.colors.gray[2]
                    }`,
                    textAlign: "left",
                  }}
                >
                  <ThemeIcon
                    size={36}
                    radius="md"
                    style={{
                      flexShrink: 0,
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[5]
                          : theme.colors.gray[1],
                      color: theme.colorScheme === "dark" ? theme.white : "#000",
                    }}
                  >
                    <Icon
                      size={20}
                      stroke={1.8}
                      color={theme.colorScheme === "dark" ? theme.white : "#000"}
                    />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} fz={14} lh={1.3}>
                      {benefit.title}
                    </Text>
                    <Text fz={12} c={mutedText} lh={1.4} mt={2}>
                      {benefit.description}
                    </Text>
                  </Box>
                </Group>
              );
            })}
          </Stack>
        )}

      </Box>

      {/* Footer */}
      {showFooter && (
        <Box
          style={{
            padding: "10px 24px 16px",
            flexShrink: 0,
          }}
        >
          <Button
            size="lg"
            fullWidth
            onClick={onContinue}
            loading={isLoading}
            disabled={isLoading}
            leftIcon={
              showButtonIcon && !isLoading ? (
                <IconFingerprint size={20} stroke={1.8} />
              ) : undefined
            }
          >
            {copy.primaryButton}
          </Button>
          {copy.skipButton && !isLoading && (
            <Box style={{ textAlign: "center", marginTop: 12 }}>
              <Anchor
                component="button"
                type="button"
                onClick={onSkip}
                c={accentColor}
                fz={14}
                fw={500}
              >
                {copy.skipButton}
              </Anchor>
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default PasskeyPrompt;
